/**
 * Later (`/{ws}/later`, Slack's saved items + reminders; logic/later): my saved messages and reminders in three tabs
 * (In progress / Archived / Completed), and the message menu's "Save for later" / "Remind me about this". One per
 * workspace, shared by the Later screen and the conversations (which show "Saved for later" on saved messages).
 * Changes are optimistic; a refused one is undone by refetching. A realtime `notification` about a reminder (or the app
 * coming back) refetches, so a reminder that fired shows as overdue. Online only.
 */
import type { LaterItem, LaterState, NotificationEvent, ReminderRepeat } from "@bundle/contracts";
import { isOverdue, itemForMessage, laterTab, overdueCount, patchItem, presetTime, removeItem, tabCounts, upsertItem, type RemindPreset } from "../logic/later.ts";
import { messagePreview } from "../logic/messages.ts";
import { messageUrl } from "../logic/shell.ts";
import { channelTitle } from "../logic/sidebar.ts";
import { memberName } from "../logic/workspace.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type LaterRow = {
  id: string;
  item: LaterItem;
  /** A text reminder (`/remind`), not a saved message. */
  isText: boolean;
  /** "#design", a DM's people, or "" for a text reminder. */
  conversation: string;
  isDm: boolean;
  authorId?: string;
  authorName?: string;
  /** One line: the message (mentions by name) or the reminder's text; "" when the message is gone. */
  preview: string;
  /** The saved message was deleted or can no longer be read. */
  gone: boolean;
  /** The reminder is due (In progress only). */
  overdue: boolean;
  /** Opens the message in context (absent for text reminders and gone messages). */
  url?: string;
  /** A text reminder someone else set for me: their name. */
  fromName?: string;
  /** A reminder I set for a conversation: "#design" (or a DM's people). */
  forConversation?: string;
};

export type LaterViewState = {
  status: "loading" | "ready" | "failed";
  tab: LaterState;
  items: LaterItem[];
  /** The current tab's rows. */
  rows: LaterRow[];
  counts: Record<LaterState, number>;
  /** Due reminders (the sidebar badge). */
  overdue: number;
  offline: boolean;
};

/** Longest wait before re-deriving overdue state (a reminder further out re-arms the timer). */
const MAX_TICK_MS = 60 * 60_000;

export class LaterViewModel extends ViewModel<LaterViewState> {
  private inFlight: Promise<void> | undefined;
  private again = false;
  private cancelTick: (() => void) | undefined;

  constructor(private readonly ws: WorkspaceViewModel) {
    super({ status: "loading", tab: "in_progress", items: [], rows: [], counts: { in_progress: 0, archived: 0, completed: 0 }, overdue: 0, offline: !ws.state.network });
    this.onDispose(() => this.cancelTick?.());
    this.onDispose(ws.subscribe(() => this.derive(this.state.items)));
  }

  /** Fetches the list (one request at a time; a call during one runs once more after it). */
  refresh(): Promise<void> {
    if (this.inFlight !== undefined) {
      this.again = true;
      return this.inFlight;
    }
    this.inFlight = this.ws.deps.api.listLater(this.ws.wsId).then(
      (items) => {
        if (this.isDisposed) return;
        this.update({ status: "ready" });
        this.derive(items);
      },
      () => {
        if (!this.isDisposed && this.state.status === "loading") this.update({ status: "failed" });
      },
    );
    return this.inFlight.finally(() => {
      this.inFlight = undefined;
      if (this.again) {
        this.again = false;
        void this.refresh();
      }
    });
  }

  retry(): void {
    this.update({ status: "loading" });
    void this.refresh();
  }

  setTab(tab: LaterState): void {
    if (tab === this.state.tab) return;
    this.update({ tab });
    this.derive(this.state.items);
  }

  /** A realtime notification: a reminder that fired refetches (it now shows as overdue). */
  onNotification(event: NotificationEvent): void {
    if (event.about === "reminder") void this.refresh();
  }

  /** The item saving this message (the row's "Saved for later" line and menu entry). */
  savedItem(messageId: string): LaterItem | undefined {
    return itemForMessage(this.state.items, messageId);
  }

  /** "Save for later" ↔ "Remove from Later" on a message. → true when it is saved now. */
  toggleSaved(messageId: string, channelId: string): boolean {
    const existing = this.savedItem(messageId);
    if (existing !== undefined && existing.state === "in_progress") {
      this.remove(existing.id);
      return false;
    }
    this.saveMessage(messageId, channelId);
    return true;
  }

  /** Saves a message (back to In progress if it was archived or completed); `remindAt` adds a reminder. */
  saveMessage(messageId: string, channelId: string, remindAt?: number): Promise<LaterItem | undefined> {
    const now = this.ws.deps.clock.now();
    const existing = this.savedItem(messageId);
    const optimistic: LaterItem =
      existing === undefined
        ? { id: `pending:${messageId}`, state: "in_progress", messageId, channelId, savedAt: now, updatedAt: now, ...(remindAt === undefined ? {} : { remindAt }) }
        : patchItem(existing, { state: "in_progress", ...(remindAt === undefined ? {} : { remindAt }) }, now);
    const m = this.findMessage(messageId);
    if (m !== undefined && optimistic.message === undefined) optimistic.message = m;
    this.derive(upsertItem(this.state.items, optimistic));
    const req = remindAt === undefined ? { messageId } : { messageId, remindAt };
    return this.ws.deps.api.saveLater(this.ws.wsId, req).then(
      (item) => {
        this.derive(upsertItem(removeItem(this.state.items, optimistic.id), item));
        return item;
      },
      () => {
        void this.refresh();
        return undefined;
      },
    );
  }

  /** "Remind me about this" on a message: saves it with a reminder at a preset time. → when it will remind. */
  remindPreset(messageId: string, channelId: string, preset: RemindPreset): number {
    const now = this.ws.deps.clock.now();
    const at = presetTime(preset, now, this.ws.deps.clock.tzOffsetMinutes(now));
    void this.saveMessage(messageId, channelId, at);
    return at;
  }

  /** `/remind me <what> <when>`: a text reminder. Rejects when the server refuses. */
  /**
   * `/remind`: a text reminder for me, someone else (`forUserId`: it lands in their Later, not mine) or a conversation
   * (`forChannelId`: kept here, posted there when due), optionally recurring.
   */
  async addTextReminder(text: string, remindAt: number, opts: { repeat?: ReminderRepeat; forUserId?: string; forChannelId?: string } = {}): Promise<LaterItem> {
    const item = await this.ws.deps.api.saveLater(this.ws.wsId, { text, remindAt, ...opts });
    if (opts.forUserId === undefined || opts.forUserId === this.ws.state.meId) this.derive(upsertItem(this.state.items, item));
    return item;
  }

  /** Stops a recurring reminder (its current time stays). */
  stopRepeat(id: string): void {
    this.patch(id, { stopRepeat: true });
  }

  complete(id: string): void {
    this.patch(id, { state: "completed" });
  }

  archive(id: string): void {
    this.patch(id, { state: "archived" });
  }

  moveToInProgress(id: string): void {
    this.patch(id, { state: "in_progress" });
  }

  /** Sets or snoozes the reminder (a preset or an exact time). */
  remind(id: string, when: RemindPreset | number): void {
    const now = this.ws.deps.clock.now();
    const at = typeof when === "number" ? when : presetTime(when, now, this.ws.deps.clock.tzOffsetMinutes(now));
    this.patch(id, { remindAt: at, state: "in_progress" });
  }

  clearReminder(id: string): void {
    this.patch(id, { remindAt: null });
  }

  remove(id: string): void {
    if (!this.state.items.some((i) => i.id === id)) return;
    this.derive(removeItem(this.state.items, id));
    if (id.startsWith("pending:")) return;
    this.ws.deps.api.deleteLater(this.ws.wsId, id).catch(() => void this.refresh());
  }

  private patch(id: string, p: { state?: LaterState; remindAt?: number | null; stopRepeat?: boolean }): void {
    const item = this.state.items.find((i) => i.id === id);
    if (item === undefined || id.startsWith("pending:")) return;
    this.derive(upsertItem(this.state.items, patchItem(item, p, this.ws.deps.clock.now())));
    this.ws.deps.api.updateLater(this.ws.wsId, id, p).then(
      // Unless it was removed meanwhile.
      (next) => {
        if (this.state.items.some((i) => i.id === next.id)) this.derive(upsertItem(this.state.items, next));
      },
      () => void this.refresh(),
    );
  }

  private findMessage(messageId: string) {
    return this.state.items.find((i) => i.messageId === messageId)?.message;
  }

  private derive(items: LaterItem[]): void {
    if (this.isDisposed) return;
    const now = this.ws.deps.clock.now();
    this.update({
      items,
      rows: this.rows(laterTab(items, this.state.tab), now),
      counts: tabCounts(items),
      overdue: overdueCount(items, now),
      offline: !this.ws.state.network,
    });
    this.armTick(items, now);
  }

  /** Re-derives when the next reminder comes due, so it turns overdue on time. */
  private armTick(items: LaterItem[], now: number): void {
    this.cancelTick?.();
    this.cancelTick = undefined;
    let next = Number.POSITIVE_INFINITY;
    for (const i of items) if (i.state === "in_progress" && i.remindAt !== undefined && i.remindAt > now) next = Math.min(next, i.remindAt);
    if (next === Number.POSITIVE_INFINITY) return;
    this.cancelTick = this.ws.deps.clock.setTimer(Math.min(next - now + 50, MAX_TICK_MS), () => {
      this.cancelTick = undefined;
      this.derive(this.state.items);
    });
  }

  private rows(items: LaterItem[], now: number): LaterRow[] {
    const cache = this.ws.state.cache;
    const ctx = { meId: this.ws.state.meId, members: cache?.members ?? {}, channels: cache?.channels ?? {} };
    return items.map((item) => {
      const isText = item.messageId === undefined;
      const m = item.message;
      const c = item.channelId === undefined ? undefined : cache?.channels[item.channelId];
      const isDm = c !== undefined && (c.kind === "dm" || c.kind === "group_dm");
      const row: LaterRow = {
        id: item.id,
        item,
        isText,
        conversation: c === undefined || cache === undefined ? "" : isDm ? channelTitle(cache, c.id) : `#${c.name}`,
        isDm,
        preview: isText ? (item.text ?? "") : m === undefined ? "" : messagePreview(m, ctx, 280),
        gone: !isText && m === undefined,
        overdue: isOverdue(item, now),
      };
      if (item.fromUserId !== undefined && cache !== undefined) row.fromName = memberName(cache, item.fromUserId);
      if (item.forChannelId !== undefined && cache !== undefined) {
        const fc = cache.channels[item.forChannelId];
        row.forConversation = fc === undefined ? "" : fc.kind === "dm" || fc.kind === "group_dm" ? channelTitle(cache, fc.id) : `#${fc.name}`;
      }
      if (m !== undefined) {
        row.authorId = m.authorId;
        if (cache !== undefined) row.authorName = memberName(cache, m.authorId);
        if (cache !== undefined) row.url = messageUrl(cache.workspace.slug, m.channelId, m.id, m.threadRootId);
      }
      return row;
    });
  }
}
